/**
 * AuthImage — 帶 JWT 的 <img> 替代元件
 *
 * 瀏覽器 <img src> 不會送 Authorization header，所以受保護的圖片 endpoint 直接
 * 用 <img> 會 401。這個元件用 axios（有 Auth interceptor）抓成 blob URL 再顯示。
 */

import React, { useEffect, useState } from 'react';
import axios from '../api/axios';

interface AuthImageProps {
  src: string;                    // 後端 API 路徑（例如 /api/ee/ops-center/materials/1/image）
  alt?: string;
  style?: React.CSSProperties;
  className?: string;
  onError?: () => void;
  onLoad?: () => void;
  fallback?: React.ReactNode;     // 載入失敗時顯示
}

const AuthImage: React.FC<AuthImageProps> = ({
  src, alt, style, className, onError, onLoad, fallback = null,
}) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let currentBlob: string | null = null;

    setFailed(false);
    setBlobUrl(null);

    (async () => {
      try {
        const r = await axios.get(src, { responseType: 'blob' });
        if (cancelled) return;
        currentBlob = URL.createObjectURL(r.data);
        setBlobUrl(currentBlob);
        onLoad?.();
      } catch {
        if (cancelled) return;
        setFailed(true);
        onError?.();
      }
    })();

    return () => {
      cancelled = true;
      if (currentBlob) URL.revokeObjectURL(currentBlob);
    };
  }, [src, onError, onLoad]);

  if (failed) return <>{fallback}</>;
  if (!blobUrl) return <>{fallback}</>;

  return <img src={blobUrl} alt={alt} style={style} className={className} />;
};

export default AuthImage;
